/**
 * Choices about how the app behaves, rather than about what is recorded.
 *
 * Kept in core and not in the web app because the native app will make the same
 * choices, and a preference read from a different key on each platform is a
 * preference that silently resets when someone moves device and restores a
 * backup. They ride in the `SettingsStore`, so a backup carries them.
 *
 * The reader is deliberately forgiving: an unreadable value, a missing key or a
 * field of the wrong type falls back to the default rather than failing. A
 * preference is not data the user typed — losing one costs a tap to set again,
 * and refusing to load the app over it would be absurd.
 */

import type { Storage } from '../ports/storage.js'

export const PREFERENCES_KEY = 'preferences'

export interface Preferences {
  /**
   * Whether logging ends on a step of its own for tags and a note.
   *
   * **On by default**, which is the whole reason it exists. The alternative —
   * tags tucked under the last question's control, and no note field at all —
   * is not a smaller version of this, it is the version people scroll past. A
   * note written a week later from memory is a different, worse note, and the
   * only moment the context is free is the moment of recording.
   *
   * It is a setting rather than a rule because the cost lands on the people who
   * log most: somebody recording six times a day does not want a second screen
   * they have nothing to put in each time.
   */
  readonly finishStep: boolean
  /**
   * Noticings the user has hidden.
   *
   * Ids rather than a blanket off switch: hiding one observation is a statement
   * about that observation, and turning the whole list off because one entry was
   * unwelcome loses the rest. A rule that fires again on new data produces a new
   * id, so a dismissal does not silence a rule for ever.
   */
  readonly hiddenNoticings: readonly string[]
}

export const DEFAULT_PREFERENCES: Preferences = {
  finishStep: true,
  hiddenNoticings: [],
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function parsePreferences(raw: string | undefined): Preferences {
  if (raw === undefined) {
    return DEFAULT_PREFERENCES
  }
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return DEFAULT_PREFERENCES
  }
  if (!isRecord(parsed)) {
    return DEFAULT_PREFERENCES
  }
  const finishStep = parsed['finishStep']
  const hidden = parsed['hiddenNoticings']
  return {
    finishStep:
      typeof finishStep === 'boolean' ? finishStep : DEFAULT_PREFERENCES.finishStep,
    hiddenNoticings: Array.isArray(hidden)
      ? hidden.filter((id): id is string => typeof id === 'string')
      : DEFAULT_PREFERENCES.hiddenNoticings,
  }
}

export async function readPreferences(storage: Storage): Promise<Preferences> {
  return parsePreferences(await storage.settings.get(PREFERENCES_KEY))
}

export async function writePreferences(
  storage: Storage,
  next: Preferences,
): Promise<void> {
  await storage.settings.set(PREFERENCES_KEY, JSON.stringify(next))
}
